/**
 * sprites.js — Character and board art for Sky-Drop.
 *
 * The art is a library of independent poses plus a standalone hoverboard, all
 * generated and aligned by `tools/build_sprites.py`. Two consequences of that
 * shape the API, and every caller has to know both:
 *
 * 1. The board is its own sprite, not part of the rider. One object serves
 *    three roles — composited under the boots while riding, held out in front
 *    while aiming, and tumbling free once kicked away — so it can carve against
 *    the body, spin independently, and be aimed as a cannon. When it was baked
 *    into the riding drawing none of that was possible and it read as a slab.
 *
 * 2. Each pose is cropped to its own pixels, so no two poses share a size and
 *    they cannot be drawn from a common top-left corner. GEOMETRY carries the
 *    measured anchors — centre of mass, ground contact, grip — that let poses be
 *    swapped mid-animation without the character jumping around the screen.
 *
 * All poses face RIGHT; the renderer mirrors for left-facing. Red variants are
 * exact channel-swapped recolors of the green art, so both players have
 * identical silhouettes and neither gets a read advantage.
 */

import GEOMETRY from './assets/sprites/manifest.json';
import { MUZZLE_DX, MUZZLE_DY } from './constants';

import idleGreen from './assets/sprites/idle_green.png';
import idleRed from './assets/sprites/idle_red.png';
import walkGreen from './assets/sprites/walk_green.png';
import walkRed from './assets/sprites/walk_red.png';
import walk2Green from './assets/sprites/walk2_green.png';
import walk2Red from './assets/sprites/walk2_red.png';
import runGreen from './assets/sprites/run_green.png';
import runRed from './assets/sprites/run_red.png';
import run2Green from './assets/sprites/run2_green.png';
import run2Red from './assets/sprites/run2_red.png';
import jumpGreen from './assets/sprites/jump_green.png';
import jumpRed from './assets/sprites/jump_red.png';
import shootGreen from './assets/sprites/shoot_green.png';
import shootRed from './assets/sprites/shoot_red.png';
import recoilGreen from './assets/sprites/recoil_green.png';
import recoilRed from './assets/sprites/recoil_red.png';
import gunwalkGreen from './assets/sprites/gunwalk_green.png';
import gunwalkRed from './assets/sprites/gunwalk_red.png';
import gunwalk2Green from './assets/sprites/gunwalk2_green.png';
import gunwalk2Red from './assets/sprites/gunwalk2_red.png';
import gunjumpGreen from './assets/sprites/gunjump_green.png';
import gunjumpRed from './assets/sprites/gunjump_red.png';
import drawGreen from './assets/sprites/draw_green.png';
import drawRed from './assets/sprites/draw_red.png';
import mountGreen from './assets/sprites/mount_green.png';
import mountRed from './assets/sprites/mount_red.png';
import rideGreen from './assets/sprites/ride_green.png';
import rideRed from './assets/sprites/ride_red.png';
import carveGreen from './assets/sprites/carve_green.png';
import carveRed from './assets/sprites/carve_red.png';
import tuckGreen from './assets/sprites/tuck_green.png';
import tuckRed from './assets/sprites/tuck_red.png';
import guardGreen from './assets/sprites/guard_green.png';
import guardRed from './assets/sprites/guard_red.png';
import punchGreen from './assets/sprites/punch_green.png';
import punchRed from './assets/sprites/punch_red.png';
import punchlowGreen from './assets/sprites/punchlow_green.png';
import punchlowRed from './assets/sprites/punchlow_red.png';
import kickGreen from './assets/sprites/kick_green.png';
import kickRed from './assets/sprites/kick_red.png';
import sweepGreen from './assets/sprites/sweep_green.png';
import sweepRed from './assets/sprites/sweep_red.png';
import jumpkickGreen from './assets/sprites/jumpkick_green.png';
import jumpkickRed from './assets/sprites/jumpkick_red.png';
import downedGreen from './assets/sprites/downed_green.png';
import downedRed from './assets/sprites/downed_red.png';
import stompGreen from './assets/sprites/stomp_green.png';
import stompRed from './assets/sprites/stomp_red.png';
import poundGreen from './assets/sprites/pound_green.png';
import poundRed from './assets/sprites/pound_red.png';
import staggerGreen from './assets/sprites/stagger_green.png';
import staggerRed from './assets/sprites/stagger_red.png';
import boardGreen from './assets/sprites/board_green.png';
import boardRed from './assets/sprites/board_red.png';

const SOURCES = {
  player1: {
    idle: idleGreen,
    walk: walkGreen,
    walk2: walk2Green,
    run: runGreen,
    run2: run2Green,
    jump: jumpGreen,
    shoot: shootGreen,
    recoil: recoilGreen,
    gunwalk: gunwalkGreen,
    gunwalk2: gunwalk2Green,
    gunjump: gunjumpGreen,
    draw: drawGreen,
    mount: mountGreen,
    ride: rideGreen,
    carve: carveGreen,
    tuck: tuckGreen,
    guard: guardGreen,
    punch: punchGreen,
    punchlow: punchlowGreen,
    kick: kickGreen,
    sweep: sweepGreen,
    jumpkick: jumpkickGreen,
    downed: downedGreen,
    stomp: stompGreen,
    pound: poundGreen,
    stagger: staggerGreen,
    board: boardGreen,
  },
  player2: {
    idle: idleRed,
    walk: walkRed,
    walk2: walk2Red,
    run: runRed,
    run2: run2Red,
    jump: jumpRed,
    shoot: shootRed,
    recoil: recoilRed,
    gunwalk: gunwalkRed,
    gunwalk2: gunwalk2Red,
    gunjump: gunjumpRed,
    draw: drawRed,
    mount: mountRed,
    ride: rideRed,
    carve: carveRed,
    tuck: tuckRed,
    guard: guardRed,
    punch: punchRed,
    punchlow: punchlowRed,
    kick: kickRed,
    sweep: sweepRed,
    jumpkick: jumpkickRed,
    downed: downedRed,
    stomp: stompRed,
    pound: poundRed,
    stagger: staggerRed,
    board: boardRed,
  },
};

export { GEOMETRY };

/**
 * Where a sprite's lowest pixel row sits relative to the player's y, which is
 * the feet/ground reference the physics uses. Matches the footprint of the art
 * this set replaces, so the bodies still line up with what is drawn.
 */
export const SPRITE_BOTTOM = -4;

/**
 * How deep the boots sink into the deck when riding, in canvas pixels. Without
 * it the rider floats a hairline above the board and the two read as separate
 * objects that happen to be near each other rather than one person standing on
 * one board.
 */
export const DECK_INSET = 2;

/**
 * Two-frame gait cycles, and how many render frames each frame is held for.
 *
 * A gait is a cycle and a drawing is a single instant, so walking and running
 * alternate between opposite-phase frames. The firing pair is not a gait: it
 * plays once per shot, snapping to the recoil frame on the kick.
 */
export const CYCLES = {
  walk: { frames: ['walk', 'walk2'], hold: 9 },
  run: { frames: ['run', 'run2'], hold: 5 },
  // Aiming on the move. Slower than the empty-handed walk because Hands mode
  // caps horizontal speed well below a sprint.
  gunWalk: { frames: ['gunwalk', 'gunwalk2'], hold: 8 },
};

/**
 * How far the held board is driven back and tipped up on the firing kick.
 *
 * The recoil drawing pulls the arms in toward the chest, so a board left at the
 * muzzle would hang in mid-air in front of hands that are no longer holding it.
 * This is applied only while the recoil frame is showing.
 */
export const RECOIL_KICK = { x: -7, y: -1, rot: -0.22 };

/** Team colours used for the rim light drawn behind each sprite. */
const RIM_COLOR = { player1: '#00ff41', player2: '#ff4757' };

// slot -> pose -> HTMLImageElement (null until loaded).
const images = { player1: {}, player2: {} };
// slot -> pose -> HTMLCanvasElement: a flat, team-coloured silhouette of the
// sprite, built once on load (see getTinted).
const silhouettes = { player1: {}, player2: {} };
// image -> colour -> HTMLCanvasElement. Keyed weakly on the sprite so a tinted
// copy cannot outlive the art it was derived from.
const tintCache = new WeakMap();

/**
 * Where the held board sits, in the player's local space, when aimed as a
 * cannon.
 *
 * Derived from the muzzle rather than the other way round. MUZZLE_DX/DY are
 * gameplay geometry — the AI aims with them and the beam spawns there — so the
 * art is positioned to put the board's nose exactly on that point. Deriving the
 * muzzle from the art instead would leave the two free to drift apart, and a
 * beam that visibly leaves from somewhere other than the barrel is worse than a
 * board held a pixel off.
 */
export const HELD_BOARD = {
  x: MUZZLE_DX - GEOMETRY.board.w / 2,
  y: MUZZLE_DY,
};

/**
 * Build a solid single-coloured copy of a sprite, preserving only its alpha.
 *
 * @param {HTMLImageElement} img Loaded sprite.
 * @param {string} color CSS colour to flood the silhouette with.
 * @returns {HTMLCanvasElement} offscreen canvas the size of the sprite.
 */
function buildTinted(img, color) {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, 0, 0);
  // Keep the sprite's alpha mask, replace every visible pixel with the colour.
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  return canvas;
}

/**
 * A flat, single-coloured copy of a sprite, built once and cached.
 *
 * Two effects want these and they want them for different reasons. The rim
 * light draws a team-coloured copy behind the sprite at small offsets, which is
 * what makes the riders tell apart at a glance — the art is almost entirely
 * dark navy, so without an outline green and red read as the same character on
 * a dark cityscape. The death dissolve draws them offset sideways instead, as
 * the colour channels separating.
 *
 * @param {HTMLImageElement|HTMLCanvasElement} img Loaded sprite.
 * @param {string} color CSS colour.
 * @returns {HTMLCanvasElement} offscreen canvas the size of the sprite.
 */
export function getTinted(img, color) {
  let byColor = tintCache.get(img);
  if (!byColor) {
    byColor = new Map();
    tintCache.set(img, byColor);
  }
  let canvas = byColor.get(color);
  if (!canvas) {
    canvas = buildTinted(img, color);
    byColor.set(color, canvas);
  }
  return canvas;
}

/** Kick off sprite preloading. Call once at app start; safe to repeat. */
export function preloadSprites() {
  Object.entries(SOURCES).forEach(([slot, poses]) => {
    Object.entries(poses).forEach(([pose, src]) => {
      if (images[slot][pose]) return;
      const img = new Image();
      img.onload = () => {
        images[slot][pose] = img;
        // Derive the rim-light silhouette once, not per frame.
        silhouettes[slot][pose] = getTinted(img, RIM_COLOR[slot]);
      };
      img.src = src;
    });
  });
}

/**
 * @param {'player1'|'player2'} slot
 * @param {string} pose Key from GEOMETRY.
 * @returns {HTMLImageElement|null} the sprite, or null while loading.
 */
export function getSprite(slot, pose) {
  return images[slot][pose] || null;
}

/**
 * @param {'player1'|'player2'} slot
 * @param {string} pose Key from GEOMETRY.
 * @returns {HTMLCanvasElement|null} team-coloured silhouette, or null while loading.
 */
export function getSilhouette(slot, pose) {
  return silhouettes[slot][pose] || null;
}
